/**
 * Authentication Hook and Context for Amtrend Command Center
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import * as api from '../lib/api'

// Create the auth context
const AuthContext = createContext(null)

/**
 * Auth Provider Component
 * Wraps the application and provides authentication state
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()

  // Check for existing auth on mount
  useEffect(() => {
    checkAuth()
  }, [])

  /**
   * Check if user is already authenticated
   */
  const checkAuth = useCallback(async () => {
    if (!api.isAuthenticated()) {
      setIsLoading(false)
      return
    }

    try {
      const userData = await api.getCurrentUser()
      setUser(userData)
      setError(null)
    } catch (err) {
      // Token is invalid, clear it
      api.logout()
      setUser(null)
      setError(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Login function
   */
  const login = useCallback(async (username, password) => {
    setIsLoading(true)
    setError(null)

    try {
      await api.login(username, password)
      const userData = await api.getCurrentUser()
      setUser(userData)

      // Redirect to the page they tried to visit or chat home
      const from = location.state?.from?.pathname || '/'
      navigate(from, { replace: true })

      return { success: true }
    } catch (err) {
      const message = err.data?.detail || err.message || 'Login failed. Please check your credentials.'
      setError(message)
      return { success: false, error: message }
    } finally {
      setIsLoading(false)
    }
  }, [navigate, location])

  /**
   * Logout function
   */
  const logout = useCallback(() => {
    api.logout()
    setUser(null)
    setError(null)
    navigate('/login', { replace: true })
  }, [navigate])

  /**
   * Clear any auth errors
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Build the context value
  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    login,
    logout,
    clearError,
    checkAuth,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

/**
 * Higher-order component to require authentication
 */
export function withAuth(Component) {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth()
    const location = useLocation()
    const navigate = useNavigate()

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate('/login', { state: { from: location }, replace: true })
      }
    }, [isAuthenticated, isLoading, location, navigate])

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amtrend-blue"></div>
        </div>
      )
    }

    if (!isAuthenticated) {
      return null
    }

    return <Component {...props} />
  }
}

export default useAuth
